/**
 * The free-karcis code a finished Bilik Kotak session earns.
 *
 * Dhafin, 2026-09-22: the Kotak promo runs to the end of the year, and the
 * guest should leave the booth already holding the code for the foto karcis
 * next door — "jadi customer beneran mandiri gitu tanpa perlu dibantu tim buat
 * minta kode dan akses". Today the team hands them out one at a time.
 *
 * Everything that decides WHETHER a code exists lives on the server
 * (`POST /api/promos/session-reward`): which project pairs with which, that
 * the session actually settled, the 24-hour life, the quota of one, and the
 * idempotency that makes a reloaded result screen show the same code rather
 * than mint a second. The booth asks and displays.
 *
 * This module is the asking. It is deliberately quiet: the result screen is
 * the last thing a guest sees, and no failure here — offline booth, 500,
 * half-deployed backend — may show them an error about a voucher they were
 * never promised. A request that does not come back with a code is a result
 * screen that simply does not mention one.
 */

export interface KarcisRewardVoucher {
  code: string
  /** ISO timestamp, or null on a voucher with no expiry configured. */
  expiresAt: string | null
}

interface RewardResponse {
  eligible?: boolean
  reason?: unknown
  code?: unknown
  expiresAt?: unknown
  reused?: unknown
}

export interface KarcisRewardResult {
  voucher: KarcisRewardVoucher | null
  /**
   * Worth asking again in a moment.
   *
   * One real race, and it is the common case: the result screen renders as soon
   * as the strip is printed, while the upload that CREATES the session document
   * server-side may still be in the store's queue. Ask in that window and the
   * honest answer is `session_not_found` for a session that exists. A transport
   * failure is retryable for the same reason — venue Wi-Fi is not reliable and
   * this is a one-shot offer.
   *
   * A session whose project is simply not part of a pairing is NOT retryable;
   * asking again would never change the answer.
   */
  retryable: boolean
}

/**
 * Which booths bother asking at all.
 *
 * A hint, not the rule — the server decides eligibility from the project
 * pairing, and it is the only thing that can. This exists so the twenty-odd
 * ordinary `bahela` projects that share this result screen do not each fire a
 * POST per session to be told no.
 */
export const KARCIS_REWARD_SOURCE_PATHS = ['bahelakotak']

/** Does this booth's customPath ever earn a reward? */
export function canEarnKarcisReward(customPath: string | null | undefined): boolean {
  return !!customPath && KARCIS_REWARD_SOURCE_PATHS.includes(customPath)
}

/** Nothing, and no point asking again. */
const NO_REWARD: KarcisRewardResult = { voucher: null, retryable: false }

/**
 * Ask for this session's reward.
 *
 * Every "no" — ineligible, unreachable, malformed — comes back as a null
 * voucher, because the screen treats them alike. `retryable` is the only thing
 * that distinguishes them, and only the caller's retry loop reads it.
 */
export async function fetchKarcisReward(
  sessionId: string | undefined | null,
  projectId: string | undefined | null,
  options?: { signal?: AbortSignal }
): Promise<KarcisRewardResult> {
  if (!sessionId || !projectId) return NO_REWARD

  try {
    const { boothAuthHeaders } = await import('@renderer/utils/boothApiAuth')
    const response = await fetch(
      `${process.env.VITE_API_URL}/api/promos/session-reward`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(await boothAuthHeaders())
        },
        body: JSON.stringify({ sessionId, projectId }),
        signal: options?.signal
      }
    )

    // 5xx is the backend having a bad moment, not a verdict on this session.
    if (!response.ok) return { voucher: null, retryable: response.status >= 500 }
    const data = (await response.json().catch(() => null)) as RewardResponse | null
    return readKarcisReward(data)
  } catch (error) {
    // Includes the AbortError from a result screen that timed out mid-request;
    // that one is retryable in principle and moot in practice, because the
    // caller that aborted is gone.
    console.warn('[karcis-reward] could not fetch the session reward:', error)
    return { voucher: null, retryable: true }
  }
}

/**
 * The parsing half, so the shape checks are testable without a network.
 *
 * A response that says eligible but carries no usable code is treated as no
 * reward: an empty box where a code should be is worse for the guest than no
 * box at all, and it is the shape a half-deployed backend would produce.
 */
export function readKarcisReward(
  data: RewardResponse | null | undefined
): KarcisRewardResult {
  if (!data?.eligible) {
    return { voucher: null, retryable: data?.reason === 'session_not_found' }
  }
  const code = typeof data.code === 'string' ? data.code.trim() : ''
  if (!code) return NO_REWARD
  return {
    voucher: {
      code,
      expiresAt:
        typeof data.expiresAt === 'string' && data.expiresAt ? data.expiresAt : null
    },
    retryable: false
  }
}

/**
 * "berlaku sampai 21.30 besok" — the sentence under the code.
 *
 * Shown as a wall-clock time rather than "24 jam lagi" because the guest is
 * deciding whether to walk next door now or come back, and a countdown they
 * read at the booth is stale by the time they are outside. `Asia/Jakarta` is
 * not pinned: the booth's own clock is the one the guest is standing in.
 */
export function formatRewardExpiry(
  expiresAt: string | null,
  now: Date = new Date()
): string | null {
  if (!expiresAt) return null
  const expiry = new Date(expiresAt)
  if (Number.isNaN(expiry.getTime())) return null

  const time = expiry.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit'
  })

  // Whole days apart on the CALENDAR, not hours apart: a session that ends at
  // 23.50 expires at 23.50 tomorrow, which is 24 hours away and two sleeps
  // later by the only reckoning the guest uses.
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const daysAway = Math.round(
    (startOfDay(expiry) - startOfDay(now)) / (24 * 3600 * 1000)
  )

  if (daysAway <= 0) return `hari ini pukul ${time}`
  if (daysAway === 1) return `besok pukul ${time}`
  // ttlHours is configurable; past tomorrow, "besok" would simply be false.
  const date = expiry.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long'
  })
  return `${date} pukul ${time}`
}
